import React from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { SongInfo } from '@components/Song/MintComponents/MintComponent/SongInfo';
import { useAuth } from '@context/auth/base';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { BondingCurveMintDataFragmentDoc } from '@graphql/reactQuery';
import { useNumberOfTokensOwned, useSamInfo } from '@store/sdk/samReactQuery';
import type { PlaylistReferral } from '@utils/types';
import { BondingCurveMintComponent } from './BondingMintComponent';
import { SaleDetails } from './SaleDetails';

const useBondingCurveMintData = ({
  contractAddress,
  chainId,
}: {
  contractAddress: string;
  chainId: number;
}) => {
  const { data: samInfo } = useSamInfo({
    editionAddress: contractAddress,
    chainId,
  });

  const { data: editionsOwned = 0 } = useNumberOfTokensOwned({
    editionAddress: contractAddress,
    chainId,
  });

  return { supply: samInfo?.supply || 0, editionsOwned };
};

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment BondingCurveMintData on Release {
    id
    chainId
    goldenEggNft {
      id
      owner {
        id
      }
    }
    ...BondingCurveMintComponent
    ...BondingCurveSaleDetailsInfo
    ...MintComponentSongInfo
  }
`);

export const BondingCurve = ({
  data,
  contractAddress,
  collectButtonSource,
  isInModal = false,
  playlistReferral,
}: {
  data: FragmentType<BondingCurveMintDataFragmentDoc>;
  contractAddress: string;
  collectButtonSource: CollectButtonSource;
  isInModal?: boolean;
  playlistReferral: PlaylistReferral | null;
}) => {
  const release = getFragment(BondingCurveMintDataFragmentDoc, data);
  const { chainId, goldenEggNft } = release;
  const { supply, editionsOwned } = useBondingCurveMintData({
    contractAddress,
    chainId: chainId,
  });
  const { authUser } = useAuth();

  const hasGoldenEgg = !!authUser && !!goldenEggNft && goldenEggNft.owner.id === authUser.id;

  return (
    <div className="flex flex-col">
      <div
        className={clsx(
          'w-full overflow-hidden rounded-t-lg bg-white',
          isInModal ? 'flex border-b border-base200 pb-4' : 'hidden p-4 shadow-tertiary lt:flex',
        )}
      >
        <SongInfo release={release} />
      </div>
      <div
        className={clsx(
          'z-above1 flex flex-col overflow-hidden',
          isInModal ? 'pt-4' : 'rounded-t-lg shadow-tertiary lt:rounded-t-none',
        )}
      >
        <BondingCurveMintComponent
          data={release}
          editionsOwned={editionsOwned}
          hasGoldenEgg={hasGoldenEgg}
          maxLiquidationThreshold={supply}
          collectButtonSource={collectButtonSource}
          playlistReferral={playlistReferral}
        />
      </div>
      {!isInModal && <SaleDetails data={release} />}
    </div>
  );
};
